import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Building2, Truck, MapPin, Phone, Mail, Key, LogIn, UserPlus,
  Car, Clock, DollarSign, CheckCircle, XCircle, AlertCircle,
  Plus, RefreshCw, Loader2, FileText, Settings, BarChart3,
  Zap, Upload, Link, Code, Shield, Eye, ArrowRight, ChevronRight,
  TrendingUp, Activity, Briefcase, Timer, Star
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Service Types
const SERVICE_TYPES = [
  { value: 'tow', label: 'Tow Service', icon: Truck, color: 'blue' },
  { value: 'jump_start', label: 'Jump Start', icon: Zap, color: 'yellow' },
  { value: 'flat_tire', label: 'Flat Tire', icon: Car, color: 'green' },
  { value: 'lockout', label: 'Lockout', icon: Key, color: 'purple' },
  { value: 'fuel_delivery', label: 'Fuel Delivery', icon: Truck, color: 'orange' },
  { value: 'winch', label: 'Winch Out', icon: Truck, color: 'red' }
];

// Auth Context for Insurance Companies
const InsuranceAuthContext = React.createContext(null);

function useInsuranceAuth() {
  const [token, setToken] = useState(localStorage.getItem('insurance_token'));
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCompanyProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCompanyProfile = async () => {
    try {
      const response = await axios.get(`${API}/insurance/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompany(response.data);
    } catch (error) {
      localStorage.removeItem('insurance_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/insurance/login`, { email, password });
    localStorage.setItem('insurance_token', response.data.token);
    setToken(response.data.token);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('insurance_token');
    setToken(null);
    setCompany(null);
  };

  const getAuthHeaders = () => ({ Authorization: `Bearer ${token}` });

  return { token, company, loading, login, logout, getAuthHeaders, fetchCompanyProfile };
}

// Login/Register Component
function InsuranceAuth({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_name: '',
    phone: '',
    contact_name: '',
    address: '',
    api_integration: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await onLogin(formData.email, formData.password);
        toast.success('Login successful!');
      } else {
        await axios.post(`${API}/insurance/register`, formData);
        toast.success('Registration successful! Your account is pending approval.');
        setIsLogin(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-white">Insurance Portal</h1>
          <p className="text-blue-200 mt-2">Jiffys Mobile Partner Platform</p>
        </div>

        <Card className="shadow-2xl">
          <CardHeader>
            <CardTitle>{isLogin ? 'Sign In' : 'Register Company'}</CardTitle>
            <CardDescription>
              {isLogin 
                ? 'Access your insurance company dashboard' 
                : 'Create an account to start dispatching jobs'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData(p => ({ ...p, company_name: e.target.value }))}
                      placeholder="ABC Insurance Co."
                      required={!isLogin}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Contact Person</Label>
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => setFormData(p => ({ ...p, contact_name: e.target.value }))}
                      placeholder="John Smith"
                      required={!isLogin}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      required={!isLogin}
                    />
                  </div>
                </>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="dispatch@insurance.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                  placeholder="********"
                  required
                />
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label>Integration Source (Optional)</Label>
                  <Select 
                    value={formData.api_integration} 
                    onValueChange={(v) => setFormData(p => ({ ...p, api_integration: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select integration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="swoop">Swoop</SelectItem>
                      <SelectItem value="towbook">Towbook</SelectItem>
                      <SelectItem value="da_drivers">D.A. Drivers App</SelectItem>
                      <SelectItem value="custom">Custom API</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : isLogin ? (
                  <LogIn className="w-4 h-4 mr-2" />
                ) : (
                  <UserPlus className="w-4 h-4 mr-2" />
                )}
                {isLogin ? 'Sign In' : 'Register'}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="justify-center">
            <Button variant="link" onClick={() => setIsLogin(!isLogin)}>
              {isLogin ? "Don't have an account? Register" : 'Already have an account? Sign In'}
            </Button>
          </CardFooter>
        </Card>

        {/* Features List */}
        <div className="mt-8 grid grid-cols-2 gap-4 text-white text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span>Direct Job Dispatch</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span>Real-time Tracking</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span>API Integration</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span>Bulk Import</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Create Job Dialog
function CreateJobDialog({ isOpen, onClose, onJobCreated, getAuthHeaders }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    service_type: 'tow',
    customer_name: '',
    customer_phone: '',
    policy_number: '',
    claim_number: '',
    vehicle_year: '',
    vehicle_make: '',
    vehicle_model: '',
    pickup_address: '',
    dropoff_address: '',
    notes: '',
    priority: 'normal'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/insurance/jobs/create`, formData, {
        headers: getAuthHeaders()
      });
      toast.success(`Job created! ID: ${response.data.job_id}`);
      onJobCreated(response.data);
      onClose();
      setFormData({
        service_type: 'tow',
        customer_name: '',
        customer_phone: '',
        policy_number: '',
        claim_number: '',
        vehicle_year: '',
        vehicle_make: '',
        vehicle_model: '',
        pickup_address: '',
        dropoff_address: '',
        notes: '',
        priority: 'normal'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-blue-600" />
            Create New Job
          </DialogTitle>
          <DialogDescription>
            Dispatch a roadside assistance job to our provider network
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Service Type */}
          <div className="grid grid-cols-3 gap-2">
            {SERVICE_TYPES.map(service => (
              <button
                key={service.value}
                type="button"
                onClick={() => setFormData(p => ({ ...p, service_type: service.value }))}
                className={`p-3 rounded-lg border-2 transition-all ${
                  formData.service_type === service.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <service.icon className={`w-5 h-5 mx-auto mb-1 text-${service.color}-500`} />
                <p className="text-xs font-medium">{service.label}</p>
              </button>
            ))}
          </div>

          {/* Priority */}
          <div className="flex gap-2">
            {['normal', 'urgent', 'emergency'].map(p => (
              <button
                key={p}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, priority: p }))}
                className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                  formData.priority === p
                    ? p === 'emergency' ? 'border-red-500 bg-red-50 text-red-700' :
                      p === 'urgent' ? 'border-orange-500 bg-orange-50 text-orange-700' :
                      'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Customer Name *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData(p => ({ ...p, customer_name: e.target.value }))}
                placeholder="John Doe"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Phone *</Label>
              <Input
                value={formData.customer_phone}
                onChange={(e) => setFormData(p => ({ ...p, customer_phone: e.target.value }))}
                placeholder="(555) 123-4567"
                required
              />
            </div>
          </div>

          {/* Policy/Claim Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Policy Number</Label>
              <Input
                value={formData.policy_number}
                onChange={(e) => setFormData(p => ({ ...p, policy_number: e.target.value }))}
                placeholder="POL-123456"
              />
            </div>
            <div className="space-y-2">
              <Label>Claim Number</Label>
              <Input
                value={formData.claim_number}
                onChange={(e) => setFormData(p => ({ ...p, claim_number: e.target.value }))}
                placeholder="CLM-789012"
              />
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Year *</Label>
              <Input
                value={formData.vehicle_year}
                onChange={(e) => setFormData(p => ({ ...p, vehicle_year: e.target.value }))}
                placeholder="2020"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Make *</Label>
              <Input
                value={formData.vehicle_make}
                onChange={(e) => setFormData(p => ({ ...p, vehicle_make: e.target.value }))}
                placeholder="Honda"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Model *</Label>
              <Input
                value={formData.vehicle_model}
                onChange={(e) => setFormData(p => ({ ...p, vehicle_model: e.target.value }))}
                placeholder="Accord"
                required
              />
            </div>
          </div>

          {/* Locations */}
          <div className="space-y-2">
            <Label>Pickup Address *</Label>
            <Input
              value={formData.pickup_address}
              onChange={(e) => setFormData(p => ({ ...p, pickup_address: e.target.value }))}
              placeholder="123 Main St, City, State ZIP"
              required
            />
          </div>

          {formData.service_type === 'tow' && (
            <div className="space-y-2">
              <Label>Dropoff Address</Label>
              <Input
                value={formData.dropoff_address}
                onChange={(e) => setFormData(p => ({ ...p, dropoff_address: e.target.value }))}
                placeholder="456 Auto Shop Rd, City, State ZIP"
              />
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Special Instructions</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
              placeholder="Any special instructions for the provider..."
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Job
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Job Card Component
function JobCard({ job }) {
  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    accepted: 'bg-blue-100 text-blue-800',
    en_route: 'bg-purple-100 text-purple-800',
    in_progress: 'bg-indigo-100 text-indigo-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-semibold text-gray-900">{job.customer_name}</p>
            <p className="text-sm text-gray-500">{job.job_id}</p>
          </div>
          <Badge className={statusColors[job.status] || statusColors.pending}>
            {job.status?.replace('_', ' ').toUpperCase()}
          </Badge>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-gray-600">
            <Truck className="w-4 h-4" />
            <span className="capitalize">{job.service_type?.replace('_', ' ')}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <Car className="w-4 h-4" />
            <span>{job.vehicle?.year} {job.vehicle?.make} {job.vehicle?.model}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-600">
            <MapPin className="w-4 h-4" />
            <span className="truncate">{job.pickup?.address}</span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <div className="flex items-center gap-1 text-green-600 font-semibold">
            <DollarSign className="w-4 h-4" />
            {job.price?.total?.toFixed(2)}
          </div>
          <span className="text-xs text-gray-400">
            {new Date(job.created_at).toLocaleString()}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

// Main Dashboard Component
function InsuranceDashboard({ company, getAuthHeaders, logout }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateJob, setShowCreateJob] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchJobs();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/insurance/dashboard`, {
        headers: getAuthHeaders()
      });
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/insurance/jobs`, {
        headers: getAuthHeaders()
      });
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJobCreated = () => {
    fetchDashboard();
    fetchJobs();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">{company?.company_name}</h1>
              <p className="text-xs text-gray-500">Insurance Partner Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={() => setShowCreateJob(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              New Job
            </Button>
            <Button variant="outline" onClick={logout}>Logout</Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Jobs
            </TabsTrigger>
            <TabsTrigger value="integrations" className="flex items-center gap-2">
              <Link className="w-4 h-4" />
              Integrations
            </TabsTrigger>
            <TabsTrigger value="api" className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              API
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{dashboard?.stats?.total_jobs || 0}</p>
                        <p className="text-sm text-gray-500">Total Jobs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                        <Activity className="w-5 h-5 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{dashboard?.stats?.active_jobs || 0}</p>
                        <p className="text-sm text-gray-500">Active Jobs</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{dashboard?.stats?.completed_jobs || 0}</p>
                        <p className="text-sm text-gray-500">Completed</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">${dashboard?.stats?.total_spend?.toFixed(0) || 0}</p>
                        <p className="text-sm text-gray-500">Total Spend</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Jobs */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Recent Jobs</CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchJobs}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                  ) : dashboard?.recent_jobs?.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No jobs yet. Create your first job!</p>
                      <Button onClick={() => setShowCreateJob(true)} className="mt-4">
                        <Plus className="w-4 h-4 mr-2" />
                        Create Job
                      </Button>
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {dashboard?.recent_jobs?.slice(0, 6).map(job => (
                        <JobCard key={job.job_id} job={job} />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>All Jobs</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchJobs}>
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button size="sm" onClick={() => setShowCreateJob(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Job
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : jobs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No jobs found</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {jobs.map(job => (
                      <JobCard key={job.job_id} job={job} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <img src="https://via.placeholder.com/24" alt="Swoop" className="rounded" />
                    Swoop Integration
                  </CardTitle>
                  <CardDescription>
                    Import jobs directly from your Swoop account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Connect your Swoop account to automatically sync tow requests to Jiffys Mobile.
                      </p>
                    </div>
                    <Button variant="outline" className="w-full">
                      <Link className="w-4 h-4 mr-2" />
                      Connect Swoop
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <img src="https://via.placeholder.com/24" alt="Towbook" className="rounded" />
                    Towbook Integration
                  </CardTitle>
                  <CardDescription>
                    Sync with your Towbook dispatching system
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Two-way sync between Towbook and Jiffys Mobile for seamless dispatching.
                      </p>
                    </div>
                    <Button variant="outline" className="w-full">
                      <Link className="w-4 h-4 mr-2" />
                      Connect Towbook
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <img src="https://via.placeholder.com/24" alt="DA" className="rounded" />
                    D.A. Drivers App
                  </CardTitle>
                  <CardDescription>
                    Integration with D.A. Drivers mobile application
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Send jobs directly to D.A. Drivers App for immediate dispatch.
                      </p>
                    </div>
                    <Button variant="outline" className="w-full">
                      <Link className="w-4 h-4 mr-2" />
                      Connect D.A. Drivers
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5 text-blue-600" />
                    Bulk Import
                  </CardTitle>
                  <CardDescription>
                    Import multiple jobs from CSV or JSON
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        Upload a file with multiple jobs to create them all at once.
                      </p>
                    </div>
                    <Button variant="outline" className="w-full">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload File
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* API Tab */}
          <TabsContent value="api">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-blue-600" />
                    API Credentials
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>API Key</Label>
                    <div className="flex gap-2">
                      <Input value={company?.api_key || '••••••••••••'} readOnly className="font-mono" />
                      <Button variant="outline" onClick={() => {
                        navigator.clipboard.writeText(company?.api_key);
                        toast.success('API key copied!');
                      }}>
                        Copy
                      </Button>
                    </div>
                  </div>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      Keep your API key secure. Do not share it publicly or commit it to version control.
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>API Documentation</CardTitle>
                  <CardDescription>Quick reference for integrating with our API</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 bg-gray-900 rounded-lg text-green-400 font-mono text-sm overflow-x-auto">
                      <p className="text-gray-500"># Create a new job</p>
                      <p>curl -X POST {API}/insurance/jobs/create \</p>
                      <p>  -H "Authorization: Bearer YOUR_TOKEN" \</p>
                      <p>  -H "Content-Type: application/json" \</p>
                      <p>  -d '{`{"service_type":"tow","customer_name":"John",...}`}'</p>
                    </div>
                    <Button variant="outline" asChild>
                      <a href={`${API}/insurance/api-docs`} target="_blank" rel="noopener noreferrer">
                        <FileText className="w-4 h-4 mr-2" />
                        View Full Documentation
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Job Dialog */}
      <CreateJobDialog
        isOpen={showCreateJob}
        onClose={() => setShowCreateJob(false)}
        onJobCreated={handleJobCreated}
        getAuthHeaders={getAuthHeaders}
      />
    </div>
  );
}

// Main Portal Component
export default function InsurancePortal() {
  const auth = useInsuranceAuth();

  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!auth.token || !auth.company) {
    return <InsuranceAuth onLogin={auth.login} />;
  }

  return (
    <InsuranceDashboard
      company={auth.company}
      getAuthHeaders={auth.getAuthHeaders}
      logout={auth.logout}
    />
  );
}
